import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Factory, Box, Users, LogOut, Package, Database, BarChart3, ShieldCheck, KeyRound, ShoppingBag, Truck, CheckSquare } from "lucide-react";

const masterItems = [
    { to: "/master/machines", label: "Machines & MHR", icon: Factory, testid: "nav-machines" },
    { to: "/master/materials", label: "Raw Materials", icon: Box, testid: "nav-materials" },
    { to: "/master/bop", label: "Bought-Out Parts", icon: ShoppingBag, testid: "nav-bop" },
    { to: "/master/vendors", label: "Outsource Vendors", icon: Truck, testid: "nav-vendors" },
    { to: "/master/manpower", label: "Manpower", icon: Users, testid: "nav-manpower" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const onLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };
    const isAdmin = user?.role === "admin";
    const isProjectManager = user?.role === "project_manager";
    // Project managers can only access the Tasks module — everything else is hidden.
    const showWorkspaceExtras = !isProjectManager;

    return (
        <div className="min-h-screen flex bg-white">
            <aside className="w-64 border-r border-zinc-200 flex flex-col no-print">
                <div className="brand-stripe" />
                <div className="p-5 border-b border-zinc-200">
                    <div className="flex flex-col items-center gap-2 text-center">
                        <img src="/macho-logo.png" alt="MACHO" className="h-20 w-auto object-contain" />
                        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Sales Champ</div>
                    </div>
                </div>

                {/* User + Sign-out moved up here so they're visible without scrolling */}
                <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2" data-testid="sidebar-account">
                    <div className="w-7 h-7 bg-[#002FA7] text-white flex items-center justify-center text-[11px] font-bold uppercase shrink-0">
                        {(user?.name || user?.email || "?").charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate" data-testid="auth-user-name">{user?.name || user?.email || "Anonymous"}</div>
                        <div className="text-[10px] text-zinc-500 truncate font-mono">{user?.role}</div>
                    </div>
                    <NavLink to="/change-password" data-testid="nav-change-password" title="Change password" className="text-zinc-400 hover:text-[#002FA7] p-1">
                        <KeyRound size={14} />
                    </NavLink>
                    <button
                        onClick={onLogout}
                        title="Sign out"
                        className="text-zinc-400 hover:text-[#E63946] p-1"
                        data-testid="logout-button"
                    >
                        <LogOut size={14} />
                    </button>
                </div>

                <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                    <div className="px-3 pt-4 pb-2 text-[10px] uppercase tracking-wider text-zinc-400 font-mono">Workspace</div>
                    {showWorkspaceExtras && (
                    <NavLink
                        to="/dashboard"
                        data-testid="nav-parts"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                isActive ? "bg-[#002FA7] text-white" : "text-zinc-700 hover:bg-zinc-100"
                            }`
                        }
                    >
                        <Package size={18} />
                        <span>Parts</span>
                    </NavLink>
                    )}
                    {showWorkspaceExtras && (
                    <NavLink
                        to="/sales"
                        data-testid="nav-sales"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                isActive ? "bg-[#002FA7] text-white" : "text-zinc-700 hover:bg-zinc-100"
                            }`
                        }
                    >
                        <BarChart3 size={18} />
                        <span>Sales Dashboard</span>
                    </NavLink>
                    )}
                    <NavLink
                        to="/tasks"
                        data-testid="nav-tasks"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                isActive ? "bg-[#002FA7] text-white" : "text-zinc-700 hover:bg-zinc-100"
                            }`
                        }
                    >
                        <CheckSquare size={18} />
                        <span>Tasks</span>
                    </NavLink>

                    {showWorkspaceExtras && (
                    <>
                    <div className="px-3 pt-6 pb-2 flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-400 font-mono" data-testid="nav-masters-header">
                        <Database size={12} />
                        <span>Masters</span>
                    </div>
                    {isAdmin && masterItems.map(({ to, label, icon: Icon, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            data-testid={testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 pl-6 pr-3 py-2 text-sm transition-colors border-l-2 ${
                                    isActive
                                        ? "bg-[#002FA7] text-white border-l-[#002FA7]"
                                        : "text-zinc-700 hover:bg-zinc-100 border-l-zinc-200"
                                }`
                            }
                        >
                            <Icon size={16} />
                            <span>{label}</span>
                        </NavLink>
                    ))}
                    {!isAdmin && <div className="pl-6 pr-3 py-2 text-xs text-zinc-400 italic">Admin-only</div>}
                    </>
                    )}

                    {isAdmin && (
                        <>
                            <div className="px-3 pt-6 pb-2 flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-400 font-mono">
                                <ShieldCheck size={12} />
                                <span>Admin</span>
                            </div>
                            <NavLink
                                to="/admin/users"
                                data-testid="nav-users"
                                className={({ isActive }) =>
                                    `flex items-center gap-3 pl-6 pr-3 py-2 text-sm transition-colors border-l-2 ${
                                        isActive
                                            ? "bg-[#E63946] text-white border-l-[#E63946]"
                                            : "text-zinc-700 hover:bg-zinc-100 border-l-zinc-200"
                                    }`
                                }
                            >
                                <Users size={16} />
                                <span>Users & roles</span>
                            </NavLink>
                        </>
                    )}
                </nav>
            </aside>

            <main className="flex-1 overflow-auto">{children}</main>
        </div>
    );
}
